import { ReactComponent as ArrowDownIcon } from 'icons/arrowDown.svg';
import { useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { date } from 'utils/date/date';
import { useTranslations } from 'utils/translations/useTranslations';
import { useAdditionalInfo } from './useAdditionalInfo';

const AD_1_INDIA_PROCESSOR = 'AD_1';

const AdditionalInfo = () => {
  const i18n = useTranslations();
  const { authorizedDealer, orderExpirationDate } = useAdditionalInfo();
  const [isOpen, setIsOpen] = useState(true);
  const containerRef = useRef<HTMLDivElement>(null);

  if (!authorizedDealer) {
    return null;
  }

  const isAd1Processor =
    authorizedDealer.processorCategory === AD_1_INDIA_PROCESSOR;
  const ad1PartnerName = isAd1Processor
    ? authorizedDealer.processorName
    : undefined;
  const calculatedMarketRate = authorizedDealer.calculatedMarketRate;
  const markupRateBasisPoints = authorizedDealer.markupRateBasisPoints
    ? `${authorizedDealer.markupRateBasisPoints} basis points`
    : undefined;

  const toggle = () => {
    const opening = !isOpen;
    flushSync(() => setIsOpen(opening));
    if (opening) {
      containerRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    }
  };

  const items = [
    {
      testId: 'forex-disclosure',
      text: i18n.t('checkout.LRS.additionalInfo.forexDisclosure'),
    },
    ad1PartnerName && {
      testId: 'ad1-tax-partner',
      text: i18n.t('checkout.LRS.additionalInfo.ad1TaxPartner', {
        partnerName: ad1PartnerName,
      }),
    },
    calculatedMarketRate != null && {
      testId: 'ibr',
      text: i18n.t('checkout.LRS.additionalInfo.ibr', {
        calculatedMarketRate: String(calculatedMarketRate),
      }),
    },
    markupRateBasisPoints && {
      testId: 'estimated-markup',
      text: i18n.t('checkout.LRS.additionalInfo.estimatedMarkup', {
        markup: markupRateBasisPoints,
      }),
    },
    {
      testId: 'delivery-days',
      text: i18n.t('checkout.LRS.additionalInfo.deliveryDays'),
    },
    orderExpirationDate && {
      testId: 'booking-expiry',
      text: i18n.t('checkout.LRS.additionalInfo.bookingExpiry', {
        date: date.formatToBrowserLocale(orderExpirationDate),
      }),
    },
  ].filter(Boolean) as { testId: string; text: string }[];

  return (
    <div ref={containerRef} className="border-t mt-2">
      <div
        role="button"
        tabIndex={0}
        className="flex items-center gap-2 text-sm font-medium bg-transparent mb-4 md:mb-0"
        onClick={toggle}
        onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && toggle()}
        aria-expanded={isOpen}
        aria-controls="lrs-additional-info-items"
        data-testid="additional-info"
      >
        {i18n.t('checkout.LRS.additionalInfo.title')}
        <ArrowDownIcon
          className={`h-3 w-3 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          aria-hidden="true"
        />
      </div>
      <div aria-live="polite" aria-atomic="true">
        {isOpen && (
          <ul
            id="additional-info-items"
            className="mt-3 pl-0 space-y-2"
            data-testid="additional-info-items"
          >
            {items.map(({ testId, text }) => (
              <li
                key={testId}
                className="flex gap-1 text-stone-600 text-xs"
                data-testid={`additional-info-item-${testId}`}
              >
                <span aria-hidden="true">•</span>
                <span>{text}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export { AdditionalInfo };
